/**
 * Cargo-weighted temperatures from get_combined_voyage_data, shared by the voyage monitoring page
 * and the home page voyage overview so both show the same figures.
 */

type TankValueMap = Record<string, unknown> | null | undefined;

/** The per-tank tank profile maps the temperature maths needs. */
export interface TankProfileTempFields {
  cargo_quantity?: TankValueMap;
  load_point?: TankValueMap;
}

/** One master log row. Only the fields the temperature maths needs. */
export interface MasterTempRow {
  record_date?: string;
  record_time?: string;
  tank_avg_temp_map?: TankValueMap;
}

const toFiniteOrNull = (value: unknown): number | null => {
  if (value == null || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Raw get_combined_voyage_data body → its tank profile (same unwrapping as the monitoring page). */
export function extractTankProfile<T extends object = TankProfileTempFields>(body: unknown): T {
  if (!body || typeof body !== 'object') {
    return {} as T;
  }
  const record = body as Record<string, any>;
  const nested =
    record['tank_profile'] ??
    record['data'] ??
    record['result'] ??
    record['response']?.['tank_profile'] ??
    record['response']?.['data'];
  return (nested && typeof nested === 'object' && !Array.isArray(nested) ? nested : record) as T;
}

/**
 * Σ(temp × cargo qty) ÷ Σ cargo qty over the tanks that have both a temperature and a positive
 * quantity; null when none do. `tankKeys` limits the tanks (defaults to every tank in `temps`).
 */
export function cargoWeightedTemp(
  temps: TankValueMap,
  quantities: TankValueMap,
  tankKeys: string[] = Object.keys(temps || {}),
): number | null {
  let weightedSum = 0;
  let totalQty = 0;
  tankKeys.forEach((tankKey) => {
    const temp = toFiniteOrNull(temps?.[tankKey]);
    const qty = toFiniteOrNull(quantities?.[tankKey]);
    if (temp == null || qty == null || qty <= 0) {
      return;
    }
    weightedSum += temp * qty;
    totalQty += qty;
  });
  return totalQty > 0 ? weightedSum / totalQty : null;
}

/** Total cargo (MT): the sum of the tanks' positive cargo quantities; 0 when nothing is loaded yet. */
export function totalCargoQuantity(profile: TankProfileTempFields): number {
  return Object.values(profile.cargo_quantity || {}).reduce<number>((sum, value) => {
    const qty = toFiniteOrNull(value);
    return qty != null && qty > 0 ? sum + qty : sum;
  }, 0);
}

/** Avg load temp: each tank's load temp weighted by its cargo quantity. */
export function averageLoadTemp(profile: TankProfileTempFields): number | null {
  return cargoWeightedTemp(profile.load_point, profile.cargo_quantity);
}

/** The latest master log that has tank temperatures (by record date, then time). */
export function lastTempLog<T extends MasterTempRow>(rows: T[]): T | null {
  const withTemps = rows.filter((row) => Object.keys(row?.tank_avg_temp_map || {}).length);
  if (!withTemps.length) {
    return null;
  }
  const stamp = (row: T) => `${row.record_date || ''} ${row.record_time || ''}`.trim();
  return withTemps.reduce((latest, row) => (stamp(row).localeCompare(stamp(latest)) >= 0 ? row : latest));
}

/** Avg discharge temp: the last master log day's tank temperatures, weighted by cargo quantity. */
export function lastDayCargoTemp(
  rows: MasterTempRow[],
  profile: TankProfileTempFields,
): { temp: number | null; date: string | null } {
  const last = lastTempLog(rows);
  return {
    temp: last ? cargoWeightedTemp(last.tank_avg_temp_map, profile.cargo_quantity) : null,
    date: last?.record_date ?? null,
  };
}
